import __var_0__ from '@stdlib/vendor/debug.js';
import __var_1__ from '@stdlib/utils/define-property';
import __var_2__ from '@stdlib/utils/keys';
import __var_3__ from '@stdlib/assert/is-array-like';
import __var_4__ from '@stdlib/utils/merge';
import __var_5__ from '@stdlib/utils/pick';
import __var_6__ from '@stdlib/utils/inherit';
import __var_7__ from '@stdlib/plot/sparklines/base/ctor';
import __var_8__ from './defaults.js';
import __var_9__ from './props/infinities/set.js';
import __var_10__ from './props/infinities/get.js';
import __var_11__ from './props/type/set.js';
import __var_12__ from './props/type/get.js';
import __var_13__ from './props/y-max/set.js';
import __var_14__ from './props/y-max/get.js';
import __var_15__ from './props/y-min/set.js';
import __var_16__ from './props/y-min/get.js';
import __var_17__ from './render.js';
/**

*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //
var logger = __var_0__;
var defineProperty = __var_1__;
var objectKeys = __var_2__;
var isArrayLike = __var_3__;
var mergeFcn = __var_4__.factory;
var pick = __var_5__;
var inherit = __var_6__;
var Sparkline = __var_7__;
var defaults = __var_8__;
var setInfinities = __var_9__;
var getInfinities = __var_10__;
var setType = __var_11__;
var getType = __var_12__;
var setYMax = __var_13__;
var getYMax = __var_14__;
var setYMin = __var_15__;
var getYMin = __var_16__;
var render = __var_17__;


// VARIABLES //

var debug = logger( 'sparkline:unicode:main' );

var merge = mergeFcn({
	'extend': false
});

// List of private properties (note: keep in alphabetical order):
var PRIVATE_PROPS = [
	'_infinities',
	'_type',
	'_yMax',
	'_yMin'
];

// List of options properties (note: keep in alphabetical order):
var OPTIONS_PROPS = [
	'infinities',
	'type',
	'yMax',
	'yMin'
];


// MAIN //

/**
* Unicode sparkline constructor.
*
* @constructor
* @param {(ArrayLike|ndarrayLike)} [data] - chart data
* @param {Options} [options] - chart options
* @param {boolean} [options.autoRender=false] - indicates whether to re-render on a `change` event
* @param {(PositiveInteger|null)} [options.bufferSize] - data buffer size
* @param {(ArrayLikeObject|ndarrayLike)} [options.data] - data
* @param {string} [options.description=''] - chart description
* @param {boolean} [options.infinities=false] - boolean indicating whether to encode infinite values
* @param {Function} [options.isDefined] - accessor indicating whether a datum is defined
* @param {string} [options.label] - data label
* @param {string} [options.type='column'] - chart type
* @param {(FiniteNumber|null)} [options.yMax] - maximum value of the y-axis domain
* @param {(FiniteNumber|null)} [options.yMin] - minimum value of the y-axis domain
* @throws {TypeError} must provide valid options
* @returns {UnicodeSparkline} chart instance
*
* @example
* var data = [ 1.0, 5.0, 3.0, 2.0, 4.0, 4.0, 3.0 ];
*
* var chart = new UnicodeSparkline( data );
*
* var str = chart.render();
* // returns '▁█▅▃▆▆▅'
*
* @example
* var data = [ 1.0, 5.0, 3.0, 2.0, 4.0, 4.0, 3.0 ];
* var opts = {
*     'data': data
* };
* var chart = new UnicodeSparkline( opts );
*
* var str = chart.render();
* // returns '▁█▅▃▆▆▅'
*/
function UnicodeSparkline() {
	var options;
	var nargs;
	var opts;
	var keys;
	var key;
	var i;

	nargs = arguments.length;
	if ( !(this instanceof UnicodeSparkline) ) {
		if ( nargs === 0 ) {
			return new UnicodeSparkline();
		}
		if ( nargs === 1 ) {
			return new UnicodeSparkline( arguments[ 0 ] );
		}
		return new UnicodeSparkline( arguments[ 0 ], arguments[ 1 ] );
	}
	// Invoke parent constructor...
	if ( nargs === 0 ) {
		Sparkline.call( this );
	} else if ( nargs === 1 ) {
		Sparkline.call( this, arguments[ 0 ] );
	} else {
		Sparkline.call( this, arguments[ 0 ], arguments[ 1 ] );
	}
	// Extract chart-specific options...
	opts = defaults();
	if ( nargs === 1 && !isArrayLike( arguments[ 0 ] ) ) {
		options = arguments[ 0 ];
	} else if ( nargs === 2 ) {
		options = arguments[ 1 ];
	} else {
		options = {};
	}
	opts = merge( opts, pick( options, OPTIONS_PROPS ) );

	debug( 'Creating an instance with the following configuration: %s.', JSON.stringify( options ) );

	// Initialize private chart-specific properties...
	for ( i = 0; i < PRIVATE_PROPS.length; i++ ) {
		defineProperty( this, PRIVATE_PROPS[i], {
			'configurable': false,
			'enumerable': false,
			'writable': true,
			'value': null
		});
	}

	// Set chart-specific properties...
	keys = objectKeys( opts );
	for ( i = 0; i < keys.length; i++ ) {
		key = keys[ i ];
		this[ key ] = opts[ key ];
	}
	return this;
}

/*
* Inherit from the `Sparkline` prototype.
*/
inherit( UnicodeSparkline, Sparkline );

/**
* Boolean indicating whether to encode infinite values.
*
* @name infinities
* @memberof UnicodeSparkline.prototype
* @type {boolean}
* @throws {TypeError} must be a boolean primitive
* @default false
*
* @example
* var chart = new UnicodeSparkline();
* chart.infinities = true;
*
* @example
* var chart = new UnicodeSparkline({
*     'infinities': true
* });
* var bool = chart.infinities;
* // returns true
*/
defineProperty( UnicodeSparkline.prototype, 'infinities', {
	'configurable': false,
	'enumerable': true,
	'set': setInfinities,
	'get': getInfinities
});

/**
* Sparkline chart type.
*
* @name types
* @memberof UnicodeSparkline.prototype
* @type {string}
* @throws {TypeError} must be a supported chart type
* @default 'column'
*
* @example
* var chart = new UnicodeSparkline();
* chart.type = 'win-loss';
*
* @example
* var chart = new UnicodeSparkline({
*     'type': 'win-loss'
* });
* var type = chart.type;
* // returns 'win-loss'
*/
defineProperty( UnicodeSparkline.prototype, 'type', {
	'configurable': false,
	'enumerable': true,
	'set': setType,
	'get': getType
});

/**
* Maximum value of the y-axis domain.
*
* @name yMax
* @memberof UnicodeSparkline.prototype
* @type {(FiniteNumber|null)}
* @throws {TypeError} must be a finite number primitive or null
*
* @example
* var chart = new UnicodeSparkline();
* chart.yMax = 100.0;
*
* @example
* var chart = new UnicodeSparkline({
*     'yMax': 314.0
* });
* var ymax = chart.yMax;
* // returns 314.0
*/
defineProperty( UnicodeSparkline.prototype, 'yMax', {
	'configurable': false,
	'enumerable': true,
	'set': setYMax,
	'get': getYMax
});

/**
* Minimum value of the y-axis domain.
*
* @name yMin
* @memberof UnicodeSparkline.prototype
* @type {(FiniteNumber|null)}
* @throws {TypeError} must be a finite number primitive or null
*
* @example
* var chart = new UnicodeSparkline();
* chart.yMin = -100.0;
*
* @example
* var chart = new UnicodeSparkline({
*     'yMin': 3.14
* });
* var ymin = chart.yMin;
* // returns 3.14
*/
defineProperty( UnicodeSparkline.prototype, 'yMin', {
	'configurable': false,
	'enumerable': true,
	'set': setYMin,
	'get': getYMin
});

/**
* Renders a sparkline chart.
*
* @name render
* @memberof UnicodeSparkline.prototype
* @type {Function}
* @returns {string} rendered sparkline chart
*
* @example
* var data = [ 1.0, 5.0, 3.0, 2.0, 4.0, 4.0, 3.0 ];
*
* var chart = new UnicodeSparkline( data );
*
* var str = chart.render();
* // returns '▁█▅▃▆▆▅'
*/
UnicodeSparkline.prototype.render = render;


// EXPORTS //
export default UnicodeSparkline;
